// Typed API surface. Each function maps one contract operation to a thin,
// well-named call. Wire shapes come from the generated schema; where the server
// preserves integers, the client reflects that with the lossless helpers.

import type { components } from './schema';
import { apiRequest, type RequestOptions } from './client';
import type { JsonBigInteger } from '@/lib/json';

type Schemas = components['schemas'];

export type Session = Schemas['Session'];
export type Providers = Schemas['Providers'];
export type SourceSummary = Schemas['SourceSummary'];
export type Device = Schemas['Device'];
export type DeviceStatus = Schemas['DeviceStatus'];
export type Channel = Schemas['Channel'];
export type Configuration = Schemas['Configuration'];
export type PutConfigurationRequest = Schemas['PutConfigurationRequest'];
export type ConfigurationAccepted = Schemas['ConfigurationAccepted'];
export type Reading = Schemas['Reading'];
export type ReadingPage = Schemas['ReadingPage'];
export type IssuedClaim = Schemas['IssuedClaim'];
export type ClaimStatus = Schemas['ClaimStatus'];
export type ExternalSourceCreated = Schemas['ExternalSourceCreated'];
export type CreateExternalSourceRequest = Schemas['CreateExternalSourceRequest'];
export type DeclaredChannel = Schemas['DeclaredChannel'];
export type UserSummary = Schemas['UserSummary'];
export type AuditEntry = Schemas['AuditEntry'];
export type RoleUpdateRequest = Schemas['RoleUpdateRequest'];
export type ImpersonateRequest = Schemas['ImpersonateRequest'];
export type AccessPolicy = Schemas['AccessPolicy'];
export type AdminDevice = Schemas['AdminDevice'];
export type DeviceCertificate = Schemas['DeviceCertificate'];
export type DeviceEnrollmentRequest = Schemas['DeviceEnrollmentRequest'];
export type DeviceEnrollment = Schemas['DeviceEnrollment'];
export type RevocationReason = 'lost' | 'compromised' | 'retired';
export type Sample = Schemas['Sample'];
export type Group = Schemas['Group'];
export type GroupDetail = Schemas['GroupDetail'];
export type GroupDatastream = Schemas['GroupDatastream'];
export type CreateGroupRequest = Schemas['CreateGroupRequest'];
export type UpdateGroupRequest = Schemas['UpdateGroupRequest'];
export type GroupMembershipRequest = Schemas['GroupMembershipRequest'];
export type GroupInvite = Schemas['GroupInvite'];
export type CreatedGroupInvite = Schemas['CreatedGroupInvite'];
export type GroupViewer = Schemas['GroupViewer'];
export type GroupInvitePreview = Schemas['GroupInvitePreview'];
export type AcceptedGroupInvite = Schemas['AcceptedGroupInvite'];
export type ZigbeeEnrollment = Schemas['ZigbeeEnrollment'];
export type CreateZigbeeEnrollmentRequest = Schemas['CreateZigbeeEnrollmentRequest'];
export type BleEnrollment = Schemas['BleEnrollment'];
export type CreateBleEnrollmentRequest = Schemas['CreateBleEnrollmentRequest'];
export type CatalogModule = Schemas['CatalogModule'];
export type PublishModuleRequest = Schemas['PublishModuleRequest'];
export type RenameSourceRequest = Schemas['RenameSourceRequest'];
export type FirmwareRelease = Schemas['FirmwareRelease'];
export type ExternalIntake = Schemas['ExternalIntake'];
export type IntakeRejection = Schemas['IntakeRejection'];
export type PublishFirmwareRequest = Schemas['PublishFirmwareRequest'];
export type DeviceFirmware = Schemas['DeviceFirmware'];
export type AvailableFirmware = Schemas['AvailableFirmware'];
export type FirmwareUpdate = Schemas['FirmwareUpdate'];
export type WifiState = Schemas['WifiState'];
export type WifiChange = Schemas['WifiChange'];
export type SealedPassword = Schemas['SealedPassword'];
export type AddWifiNetworkRequest = Schemas['AddWifiNetworkRequest'];
export type SourceRemoved = Schemas['SourceRemoved'];
export type ReadingsDeleted = Schemas['ReadingsDeleted'];

/** Cursor and telemetry id may exceed the safe integer range, so widen them. */
export type ReadingCursor = number | JsonBigInteger;

/** A reading with an exact cursor, as returned by the lossless parser. */
export interface ReadingWithCursor extends Omit<Reading, 'telemetryId' | 'value'> {
  telemetryId: ReadingCursor;
  /** Reading values are typed; a large integer arrives as a carrier object. */
  value: Reading['value'] | JsonBigInteger;
}

/** A reading page whose cursor is preserved exactly. */
export interface ReadingPageLossless extends Omit<ReadingPage, 'items' | 'nextBefore'> {
  items: ReadingWithCursor[];
  nextBefore?: ReadingCursor | null;
}

// --- Account and session -----------------------------------------------------

export function getSession(options?: RequestOptions): Promise<Session> {
  return apiRequest<Session>('/api/account/session', options);
}

export function logout(options?: RequestOptions): Promise<void> {
  return apiRequest<void>('/api/account/session', { ...options, method: 'DELETE' });
}

/** Delete the signed-in account and everything it owns (ADR 0025). */
export function deleteAccount(options?: RequestOptions): Promise<void> {
  return apiRequest<void>('/api/account', { ...options, method: 'DELETE' });
}

/**
 * Where the account export downloads from. It is a plain link rather than a
 * fetch: the browser streams the attachment to disk, however large.
 */
export const ACCOUNT_EXPORT_URL = '/api/account/export';

export function listProviders(options?: RequestOptions): Promise<Providers> {
  return apiRequest<Providers>('/api/account/providers', options);
}

/** Absolute URL of a provider login-start route, including a return path. */
export function providerStartUrl(provider: 'google' | 'apple', returnTo?: string): string {
  const query = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
  return `/auth/${provider}/start${query}`;
}

// --- Pairing claims ----------------------------------------------------------

export function createClaim(options?: RequestOptions): Promise<IssuedClaim> {
  return apiRequest<IssuedClaim>('/claims', { ...options, method: 'POST' });
}

export function getClaim(claimId: string, options?: RequestOptions): Promise<ClaimStatus> {
  return apiRequest<ClaimStatus>(`/claims/${encodeURIComponent(claimId)}`, options);
}

// --- Sources, devices, channels ---------------------------------------------

export function listSources(options?: RequestOptions): Promise<SourceSummary[]> {
  return apiRequest<SourceSummary[]>('/api/sources', options);
}

export function getSource(sourceId: string, options?: RequestOptions): Promise<SourceSummary> {
  return apiRequest<SourceSummary>(`/api/sources/${encodeURIComponent(sourceId)}`, options);
}

export function renameSource(
  sourceId: string,
  body: RenameSourceRequest,
  options?: RequestOptions,
): Promise<SourceSummary> {
  return apiRequest<SourceSummary>(`/api/sources/${encodeURIComponent(sourceId)}`, {
    ...options,
    method: 'PATCH',
    body,
  });
}

/**
 * Remove a source from the account (ADR 0014). `keep` moves its history to an
 * archive; `delete` deletes it. An archive is always deleted (pass `delete`).
 */
export function removeSource(
  sourceId: string,
  data: 'keep' | 'delete',
  options?: RequestOptions,
): Promise<SourceRemoved> {
  return apiRequest<SourceRemoved>(`/api/sources/${encodeURIComponent(sourceId)}`, {
    ...options,
    method: 'DELETE',
    query: { data },
  });
}

export function listChannels(sourceId: string, options?: RequestOptions): Promise<Channel[]> {
  return apiRequest<Channel[]>(`/api/sources/${encodeURIComponent(sourceId)}/channels`, options);
}

export function createExternalSource(
  body: CreateExternalSourceRequest,
  options?: RequestOptions,
): Promise<ExternalSourceCreated> {
  return apiRequest<ExternalSourceCreated>('/api/sources', {
    ...options,
    method: 'POST',
    body,
  });
}

export function listDevices(options?: RequestOptions): Promise<Device[]> {
  return apiRequest<Device[]>('/api/devices', options);
}

export function getDevice(publicId: string, options?: RequestOptions): Promise<Device> {
  return apiRequest<Device>(`/api/devices/${encodeURIComponent(publicId)}`, options);
}

export function getDeviceStatus(publicId: string, options?: RequestOptions): Promise<DeviceStatus> {
  return apiRequest<DeviceStatus>(`/api/devices/${encodeURIComponent(publicId)}/status`, options);
}

// --- Configuration -----------------------------------------------------------

export function getConfiguration(
  publicId: string,
  options?: RequestOptions,
): Promise<Configuration> {
  return apiRequest<Configuration>(
    `/api/devices/${encodeURIComponent(publicId)}/configuration`,
    options,
  );
}

export function putConfiguration(
  publicId: string,
  body: PutConfigurationRequest,
  options?: RequestOptions,
): Promise<ConfigurationAccepted> {
  return apiRequest<ConfigurationAccepted>(
    `/api/devices/${encodeURIComponent(publicId)}/configuration`,
    { ...options, method: 'PUT', body },
  );
}

// --- External intake ---------------------------------------------------------

export function getExternalIntake(
  sourceId: string,
  options?: RequestOptions,
): Promise<ExternalIntake> {
  return apiRequest<ExternalIntake>(`/api/sources/${encodeURIComponent(sourceId)}/intake`, options);
}

/** Replace the source's intake token; the new one is returned once. */
export function rotateIntakeToken(
  sourceId: string,
  options?: RequestOptions,
): Promise<{ intakeToken: string }> {
  return apiRequest<{ intakeToken: string }>(
    `/api/sources/${encodeURIComponent(sourceId)}/intake/token`,
    { ...options, method: 'POST' },
  );
}

// --- Firmware (ADR 0018) ----------------------------------------------------

export function getDeviceFirmware(
  publicId: string,
  options?: RequestOptions,
): Promise<DeviceFirmware> {
  return apiRequest<DeviceFirmware>(
    `/api/devices/${encodeURIComponent(publicId)}/firmware`,
    options,
  );
}

export function updateDeviceFirmware(
  publicId: string,
  releaseId: string,
  options?: RequestOptions,
): Promise<ConfigurationAccepted> {
  return apiRequest<ConfigurationAccepted>(
    `/api/devices/${encodeURIComponent(publicId)}/firmware/update`,
    { ...options, method: 'POST', body: { release_id: releaseId } },
  );
}

export function listFirmwareReleases(options?: RequestOptions): Promise<FirmwareRelease[]> {
  return apiRequest<FirmwareRelease[]>('/api/admin/firmware', options);
}

export function publishFirmwareRelease(
  body: PublishFirmwareRequest,
  options?: RequestOptions,
): Promise<FirmwareRelease> {
  return apiRequest<FirmwareRelease>('/api/admin/firmware', { ...options, method: 'POST', body });
}

export function withdrawFirmwareRelease(
  releaseId: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(`/api/admin/firmware/${encodeURIComponent(releaseId)}/withdrawal`, {
    ...options,
    method: 'POST',
  });
}

export function restoreFirmwareRelease(releaseId: string, options?: RequestOptions): Promise<void> {
  return apiRequest<void>(`/api/admin/firmware/${encodeURIComponent(releaseId)}/withdrawal`, {
    ...options,
    method: 'DELETE',
  });
}

/** Change one module instance's desired output state (ADR 0015). */
export function putDesiredState(
  publicId: string,
  instanceKey: string,
  desiredState: Record<string, boolean | number | string>,
  options?: RequestOptions,
): Promise<ConfigurationAccepted> {
  return apiRequest<ConfigurationAccepted>(
    `/api/devices/${encodeURIComponent(publicId)}/modules/${encodeURIComponent(instanceKey)}/desired-state`,
    { ...options, method: 'PUT', body: { desiredState } },
  );
}

// --- Wi-Fi -------------------------------------------------------------------

function wifiPath(publicId: string): string {
  return `/api/devices/${encodeURIComponent(publicId)}/wifi`;
}

export function getWifi(publicId: string, options?: RequestOptions): Promise<WifiState> {
  return apiRequest<WifiState>(wifiPath(publicId), options);
}

export function addWifiNetwork(
  publicId: string,
  body: AddWifiNetworkRequest,
  options?: RequestOptions,
): Promise<WifiChange> {
  return apiRequest<WifiChange>(`${wifiPath(publicId)}/networks`, {
    ...options,
    method: 'POST',
    body,
  });
}

export function removeWifiNetwork(
  publicId: string,
  ssid: string,
  options?: RequestOptions,
): Promise<WifiChange> {
  return apiRequest<WifiChange>(`${wifiPath(publicId)}/removals`, {
    ...options,
    method: 'POST',
    body: { ssid },
  });
}

// --- Module catalog ----------------------------------------------------------

export function listModules(options?: RequestOptions): Promise<CatalogModule[]> {
  return apiRequest<CatalogModule[]>('/api/modules', options);
}

export function listCatalog(options?: RequestOptions): Promise<CatalogModule[]> {
  return apiRequest<CatalogModule[]>('/api/admin/modules', options);
}

export function publishModule(
  body: PublishModuleRequest,
  options?: RequestOptions,
): Promise<CatalogModule> {
  return apiRequest<CatalogModule>('/api/admin/modules', { ...options, method: 'POST', body });
}

function deprecationPath(moduleId: string, version: string): string {
  return `/api/admin/modules/${encodeURIComponent(moduleId)}/versions/${encodeURIComponent(version)}/deprecation`;
}

export function deprecateModuleVersion(
  moduleId: string,
  version: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(deprecationPath(moduleId, version), { ...options, method: 'POST' });
}

export function restoreModuleVersion(
  moduleId: string,
  version: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(deprecationPath(moduleId, version), { ...options, method: 'DELETE' });
}

// --- Zigbee sensors ----------------------------------------------------------

function enrollmentsPath(publicId: string): string {
  return `/api/devices/${encodeURIComponent(publicId)}/zigbee/enrollments`;
}

export function listZigbeeEnrollments(
  publicId: string,
  options?: RequestOptions,
): Promise<ZigbeeEnrollment[]> {
  return apiRequest<ZigbeeEnrollment[]>(enrollmentsPath(publicId), options);
}

export function createZigbeeEnrollment(
  publicId: string,
  body: CreateZigbeeEnrollmentRequest,
  options?: RequestOptions,
): Promise<ZigbeeEnrollment> {
  return apiRequest<ZigbeeEnrollment>(enrollmentsPath(publicId), {
    ...options,
    method: 'POST',
    body,
  });
}

// --- BLE sensors -------------------------------------------------------------

function bleEnrollmentsPath(publicId: string): string {
  return `/api/devices/${encodeURIComponent(publicId)}/ble/enrollments`;
}

export function listBleEnrollments(
  publicId: string,
  options?: RequestOptions,
): Promise<BleEnrollment[]> {
  return apiRequest<BleEnrollment[]>(bleEnrollmentsPath(publicId), options);
}

export function createBleEnrollment(
  publicId: string,
  body: CreateBleEnrollmentRequest,
  options?: RequestOptions,
): Promise<BleEnrollment> {
  return apiRequest<BleEnrollment>(bleEnrollmentsPath(publicId), {
    ...options,
    method: 'POST',
    body,
  });
}

// --- Groups ------------------------------------------------------------------

export function listGroups(options?: RequestOptions): Promise<Group[]> {
  return apiRequest<Group[]>('/api/groups', options);
}

export function getGroup(groupId: string, options?: RequestOptions): Promise<GroupDetail> {
  return apiRequest<GroupDetail>(`/api/groups/${encodeURIComponent(groupId)}`, options);
}

export function createGroup(
  body: CreateGroupRequest,
  options?: RequestOptions,
): Promise<GroupDetail> {
  return apiRequest<GroupDetail>('/api/groups', { ...options, method: 'POST', body });
}

export function updateGroup(
  groupId: string,
  body: UpdateGroupRequest,
  options?: RequestOptions,
): Promise<GroupDetail> {
  return apiRequest<GroupDetail>(`/api/groups/${encodeURIComponent(groupId)}`, {
    ...options,
    method: 'PATCH',
    body,
  });
}

export function deleteGroup(groupId: string, options?: RequestOptions): Promise<void> {
  return apiRequest<void>(`/api/groups/${encodeURIComponent(groupId)}`, {
    ...options,
    method: 'DELETE',
  });
}

function groupMemberPath(groupId: string, datastreamId: string): string {
  return `/api/groups/${encodeURIComponent(groupId)}/datastreams/${encodeURIComponent(datastreamId)}`;
}

/**
 * Place a datastream in a group, or change its order or section. Fields left
 * out keep their current values; `section: null` clears the section.
 */
export function addGroupDatastream(
  groupId: string,
  datastreamId: string,
  body?: GroupMembershipRequest,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(groupMemberPath(groupId, datastreamId), {
    ...options,
    method: 'PUT',
    ...(body ? { body } : {}),
  });
}

export function removeGroupDatastream(
  groupId: string,
  datastreamId: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(groupMemberPath(groupId, datastreamId), {
    ...options,
    method: 'DELETE',
  });
}

function groupPath(groupId: string, ...rest: string[]): string {
  return ['/api/groups', groupId, ...rest]
    .map((part, index) => (index === 0 ? part : encodeURIComponent(part)))
    .join('/');
}

export function listGroupInvites(
  groupId: string,
  options?: RequestOptions,
): Promise<GroupInvite[]> {
  return apiRequest<GroupInvite[]>(groupPath(groupId, 'invites'), options);
}

/** Create a single-use invite. Its token is returned only here. */
export function createGroupInvite(
  groupId: string,
  options?: RequestOptions,
): Promise<CreatedGroupInvite> {
  return apiRequest<CreatedGroupInvite>(groupPath(groupId, 'invites'), {
    ...options,
    method: 'POST',
  });
}

export function cancelGroupInvite(
  groupId: string,
  inviteId: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(groupPath(groupId, 'invites', inviteId), {
    ...options,
    method: 'DELETE',
  });
}

export function listGroupViewers(
  groupId: string,
  options?: RequestOptions,
): Promise<GroupViewer[]> {
  return apiRequest<GroupViewer[]>(groupPath(groupId, 'viewers'), options);
}

/** Remove a viewer (the owner), or stop viewing (the viewer, with its own id). */
export function removeGroupViewer(
  groupId: string,
  userId: string,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(groupPath(groupId, 'viewers', userId), {
    ...options,
    method: 'DELETE',
  });
}

export function previewGroupInvite(
  token: string,
  options?: RequestOptions,
): Promise<GroupInvitePreview> {
  return apiRequest<GroupInvitePreview>('/api/invites/preview', {
    ...options,
    method: 'POST',
    body: { token },
  });
}

export function acceptGroupInvite(
  token: string,
  options?: RequestOptions,
): Promise<AcceptedGroupInvite> {
  return apiRequest<AcceptedGroupInvite>('/api/invites/accept', {
    ...options,
    method: 'POST',
    body: { token },
  });
}

// --- Readings ----------------------------------------------------------------

export function listReadings(
  datastreamId: string,
  params: { limit?: number; before?: string; signal?: AbortSignal } = {},
): Promise<ReadingPageLossless> {
  return apiRequest<ReadingPageLossless>(
    `/api/datastreams/${encodeURIComponent(datastreamId)}/readings`,
    {
      query: { limit: params.limit, before: params.before },
      ...(params.signal ? { signal: params.signal } : {}),
    },
  );
}

/** Delete a stream with all its readings (ADR 0014). Irreversible. */
export function deleteDatastream(datastreamId: string, options?: RequestOptions): Promise<void> {
  return apiRequest<void>(`/api/datastreams/${encodeURIComponent(datastreamId)}`, {
    ...options,
    method: 'DELETE',
  });
}

/**
 * Delete readings by observation time, `from` inclusive and `to` exclusive;
 * without either bound, every reading. Irreversible.
 */
export function deleteReadings(
  datastreamId: string,
  range: { from?: string; to?: string } = {},
  options?: RequestOptions,
): Promise<ReadingsDeleted> {
  return apiRequest<ReadingsDeleted>(
    `/api/datastreams/${encodeURIComponent(datastreamId)}/readings`,
    { ...options, method: 'DELETE', query: { from: range.from, to: range.to } },
  );
}

// --- Administration ----------------------------------------------------------

export function listUsers(
  params: { limit?: number; offset?: number } = {},
  options?: RequestOptions,
): Promise<UserSummary[]> {
  return apiRequest<UserSummary[]>('/api/admin/users', {
    ...options,
    query: { limit: params.limit, offset: params.offset },
  });
}

export function setUserRole(
  userId: string,
  body: RoleUpdateRequest,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
    ...options,
    method: 'POST',
    body,
  });
}

export function deleteUser(userId: string, options?: RequestOptions): Promise<void> {
  return apiRequest<void>(`/api/admin/users/${encodeURIComponent(userId)}`, {
    ...options,
    method: 'DELETE',
  });
}

export function setUserApproval(
  userId: string,
  approved: boolean,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(`/api/admin/users/${encodeURIComponent(userId)}/approval`, {
    ...options,
    method: 'POST',
    body: { approved },
  });
}

export function getAccessPolicy(options?: RequestOptions): Promise<AccessPolicy> {
  return apiRequest<AccessPolicy>('/api/admin/access', options);
}

export function setAccessPolicy(body: AccessPolicy, options?: RequestOptions): Promise<void> {
  return apiRequest<void>('/api/admin/access', { ...options, method: 'PUT', body });
}

export function startImpersonation(
  body: ImpersonateRequest,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>('/api/admin/impersonate', {
    ...options,
    method: 'POST',
    body,
  });
}

export function stopImpersonation(options?: RequestOptions): Promise<void> {
  return apiRequest<void>('/api/admin/impersonate', { ...options, method: 'DELETE' });
}

export function listAdminDevices(options?: RequestOptions): Promise<AdminDevice[]> {
  return apiRequest<AdminDevice[]>('/api/admin/devices', options);
}

export function enrollDevice(
  body: DeviceEnrollmentRequest,
  options?: RequestOptions,
): Promise<DeviceEnrollment> {
  return apiRequest<DeviceEnrollment>('/api/admin/devices', { ...options, method: 'POST', body });
}

export function factoryResetDevice(
  publicId: string,
  options?: RequestOptions,
): Promise<{ version: number }> {
  return apiRequest<{ version: number }>(
    `/api/admin/devices/${encodeURIComponent(publicId)}/factory-reset`,
    { ...options, method: 'POST' },
  );
}

export function revokeDeviceCertificate(
  fingerprint: string,
  reason: RevocationReason,
  options?: RequestOptions,
): Promise<void> {
  return apiRequest<void>(
    `/api/admin/device-certificates/${encodeURIComponent(fingerprint)}/revocation`,
    { ...options, method: 'POST', body: { reason } },
  );
}

export function listAudit(
  params: { limit?: number; offset?: number } = {},
  options?: RequestOptions,
): Promise<AuditEntry[]> {
  return apiRequest<AuditEntry[]>('/api/admin/audit', {
    ...options,
    query: { limit: params.limit, offset: params.offset },
  });
}
